/**
 * Breed Search Tool
 * Handles breed search, API integration, and product recommendations
 */

// ENTITY Configuration
// Determine which store to use:
// - 'BBAU' for Australia store (uses product.url_au -> barneybed.com.au)
// - 'BBUS' for USA store (uses product.url_usa -> barneybed.com)
//
// Can be set via:
// 1. URL parameter: ?entity=BBUS or ?entity=BBAU
// 2. Data attribute: <div id="breed-search-container" data-entity="BBUS">
// 3. Default fallback below
function getEntity() {
  // Check URL parameter first
  const urlParams = new URLSearchParams(window.location.search);
  const urlEntity = urlParams.get('entity');
  if (urlEntity && (urlEntity === 'BBAU' || urlEntity === 'BBUS')) {
    return urlEntity;
  }

  // Check data attribute on container
  const container = document.querySelector('[data-entity]');
  if (container) {
    const dataEntity = container.getAttribute('data-entity');
    if (dataEntity === 'BBAU' || dataEntity === 'BBUS') {
      return dataEntity;
    }
  }

  // Default to USA
  return 'BBUS';
}

const ENTITY = getEntity();
console.log('Breed Search - Store:', ENTITY === 'BBAU' ? 'Australia (barneybed.com.au)' : 'USA (barneybed.com)');

// API Configuration
// Use local API during development to avoid CORS issues
// For production deployment, change to 'https://vip.barneybed.com'
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3010'
  : 'https://vip.barneybed.com';

// Product images - will be populated from data attributes
let IMAGES = {
  BARNEY_BED: '',
  BARNEY_CURL: ''
};

// State
let searchTerm = '';
let selectedBreed = '';
let showResultsView = false;
let availableBreeds = [];
let isLoadingBreeds = false;
let debounceTimer = null;
let autoOpenTimer = null;

// DOM elements
let elements = {};

/**
 * Initialize the application
 */
function init() {
  // Cache DOM elements
  elements = {
    modal: document.getElementById('breedSearchModal'),
    searchInput: document.getElementById('searchInput'),
    clearBtn: document.getElementById('clearBtn'),
    dropdown: document.getElementById('dropdown'),
    closeBtn: document.getElementById('closeBtn'),
    backBtn: document.getElementById('backBtn'),
    searchView: document.getElementById('searchView'),
    resultsView: document.getElementById('resultsView'),
    resultsDescription: document.getElementById('resultsDescription'),
    productList: document.getElementById('productList')
  };

  // Load image URLs from data attributes
  if (elements.modal) {
    IMAGES.BARNEY_BED = elements.modal.getAttribute('data-barney-bed-image') || '';
    IMAGES.BARNEY_CURL = elements.modal.getAttribute('data-barney-curl-image') || '';
  }

  // Setup event listeners
  setupEventListeners();

  // Setup auto-open functionality for homepage
  setupAutoOpen();
}

/**
 * Setup all event listeners
 */
function setupEventListeners() {
  elements.searchInput.addEventListener('input', handleSearchInput);
  elements.searchInput.addEventListener('focus', handleSearchFocus);
  elements.searchInput.addEventListener('blur', handleSearchBlur);
  elements.clearBtn.addEventListener('click', handleClear);
  elements.backBtn.addEventListener('click', handleBack);
  elements.closeBtn.addEventListener('click', handleClose);
}

/**
 * Check if we're on the homepage
 */
function isHomepage() {
  const path = window.location.pathname;
  // Homepage is '/' or '/index' or similar variations
  return path === '/' || path === '/index' || path === '/index.html' || path === '';
}

/**
 * Check if URL has the popup parameter
 */
function hasPopupParameter() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('popup') === 'size' || urlParams.get('popup') === 'breed';
}

/**
 * Setup auto-open functionality for homepage
 */
function setupAutoOpen() {

    if (hasPopupParameter()) {
    console.log('Breed Search - URL popup parameter detected, opening immediately');
    // Open immediately when URL parameter is present
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('breed-search:auto-open'));
    }, 500); // Small delay to ensure everything is loaded
    return; // Exit early - skip all the homepage/session checks below
  }
  
  // Only run on homepage
  if (!isHomepage()) {
    console.log('Breed Search - Not on homepage, skipping auto-open');
    return;
  }

  // Check if modal was already closed in this session
  const modalClosedInSession = sessionStorage.getItem('breedSearchModalClosed');
  if (modalClosedInSession === 'true') {
    console.log('Breed Search - Modal was closed in this session, skipping auto-open');
    return;
  }

  // Set up auto-open timer for 10 seconds
  console.log('Breed Search - Setting up auto-open timer for 10 seconds');
  autoOpenTimer = setTimeout(() => {
    console.log('Breed Search - Auto-opening modal');
    // Trigger open event for parent window/modal
    window.dispatchEvent(new CustomEvent('breed-search:auto-open'));
  }, 10000); // 10 seconds
}

/**
 * Fetch breeds from API based on search query
 */
async function fetchBreeds(searchQuery) {
  try {
    isLoadingBreeds = true;
    const response = await fetch(`${API_BASE_URL}/api/breeds/search?q=${encodeURIComponent(searchQuery)}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // API returns array directly
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching breeds:', error);
    return [];
  } finally {
    isLoadingBreeds = false;
  }
}

/**
 * Fetch breed recommendation from API
 */
async function fetchBreedRecommendation(breed) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/breeds/${encodeURIComponent(breed)}/recommendation`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Breed not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching breed recommendation:', error);
    throw error;
  }
}

/**
 * Handle search input changes
 */
function handleSearchInput(e) {
  searchTerm = e.target.value;

  if (searchTerm) {
    elements.clearBtn.classList.add('show');
  } else {
    elements.clearBtn.classList.remove('show');
  }

  updateDropdown();
}

/**
 * Handle search input focus
 */
function handleSearchFocus() {
  const trimmedSearch = searchTerm.trim();

  // Show existing results if we have them and search is at least 2 chars
  if (trimmedSearch.length >= 2 && availableBreeds.length > 0) {
    elements.dropdown.classList.add('show');
    renderDropdown(availableBreeds);
  } else if (trimmedSearch.length >= 2) {
    // Trigger new search
    updateDropdown();
  }
}

/**
 * Handle search input blur
 */
function handleSearchBlur() {
  setTimeout(() => {
    elements.dropdown.classList.remove('show');
  }, 200);
}

/**
 * Update dropdown with filtered breeds
 */
function updateDropdown() {
  // Clear existing debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  const trimmedSearch = searchTerm.trim();

  // Hide dropdown if search is empty
  if (!trimmedSearch) {
    elements.dropdown.classList.remove('show');
    return;
  }

  // Don't search until at least 2 characters
  if (trimmedSearch.length < 2) {
    elements.dropdown.classList.remove('show');
    return;
  }

  // Show loading state immediately
  elements.dropdown.classList.add('show');
  isLoadingBreeds = true;
  renderDropdown([]);

  // Debounce API call (300ms delay)
  debounceTimer = setTimeout(async () => {
    try {
      // Fetch breeds from API
      const breeds = await fetchBreeds(trimmedSearch);
      availableBreeds = breeds;

      // Update dropdown with results
      renderDropdown(breeds);
    } catch (error) {
      console.error('Error in updateDropdown:', error);
      isLoadingBreeds = false;
      renderDropdown([]);
    }
  }, 300);
}

/**
 * Render dropdown with breed options
 */
function renderDropdown(breeds) {
  elements.dropdown.innerHTML = '';

  if (isLoadingBreeds) {
    elements.dropdown.innerHTML = '<div class="no-results">Loading...</div>';
  } else if (breeds.length === 0) {
    elements.dropdown.innerHTML = '<div class="no-results">No breeds found</div>';
  } else {
    breeds.forEach(breed => {
      const button = document.createElement('button');
      button.className = 'dropdown-item';
      button.textContent = breed;
      button.onclick = () => handleSelectBreed(breed);
      elements.dropdown.appendChild(button);
    });
  }
}

/**
 * Handle breed selection
 */
function handleSelectBreed(breed) {
  searchTerm = breed;
  selectedBreed = breed;
  elements.searchInput.value = breed;
  elements.dropdown.classList.remove('show');
  showResults();
}

/**
 * Show results view with recommendations
 */
async function showResults() {
  showResultsView = true;
  elements.searchView.classList.add('hide');
  elements.resultsView.classList.add('show');
  elements.backBtn.classList.add('show');

  // Update results description
  elements.resultsDescription.innerHTML = `Most <strong>${selectedBreed}</strong> owners buy the following sizes. View the product pages for more information.`;

  // Show loading state
  elements.productList.innerHTML = '<div class="no-results">Loading recommendations...</div>';

  try {
    // Fetch breed recommendation from API
    const recommendation = await fetchBreedRecommendation(selectedBreed);

    // Check if we have products
    if (!recommendation.products || recommendation.products.length === 0) {
      elements.productList.innerHTML = '<div class="no-results">No recommendations available for this breed.</div>';
      return;
    }

    // Render products
    renderProducts(recommendation.products);
  } catch (error) {
    console.error('Error loading recommendations:', error);
    elements.productList.innerHTML = '<div class="no-results">Failed to load recommendations. Please try again.</div>';
  }
}

/**
 * Render product recommendations
 */
function renderProducts(products) {
  elements.productList.innerHTML = '';

  products.forEach(product => {
    const card = document.createElement('div');
    card.className = 'product-card';

    // Determine product image based on type
    const isBarneyCurl = product.type === "BarneyCurl";
    const imageUrl = isBarneyCurl ? IMAGES.BARNEY_CURL : IMAGES.BARNEY_BED;
    const imagePadding = isBarneyCurl ? ' padded' : '';

    // Get the correct URL based on ENTITY configuration
    const productUrl = ENTITY === 'BBAU' ? product.url_au : product.url_usa;

    card.innerHTML = `
      <div class="product-image-wrapper">
        <img src="${imageUrl}" alt="${product.type}" class="product-image${imagePadding}">
      </div>
      <div class="size-badge">${product.symbol}</div>
      <div class="product-info">
        <p class="product-size-label">${product.size}</p>
        <p class="product-name">${product.type}</p>
      </div>
      <button class="view-btn">View</button>
    `;

    // Make entire card clickable
    card.style.cursor = 'pointer';
    card.addEventListener('click', () => {
      window.location.href = productUrl;
    });

    elements.productList.appendChild(card);
  });
}

/**
 * Handle clear button click
 */
function handleClear() {
  // Clear debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  searchTerm = '';
  selectedBreed = '';
  elements.searchInput.value = '';
  elements.clearBtn.classList.remove('show');
  elements.dropdown.classList.remove('show');
}

/**
 * Handle back button click
 */
function handleBack() {
  showResultsView = false;
  searchTerm = '';
  selectedBreed = '';
  elements.searchInput.value = '';
  elements.searchView.classList.remove('hide');
  elements.resultsView.classList.remove('show');
  elements.backBtn.classList.remove('show');
  elements.clearBtn.classList.remove('show');
}

/**
 * Handle close button click
 */
function handleClose() {
  searchTerm = '';
  selectedBreed = '';
  elements.searchInput.value = '';
  showResultsView = false;
  elements.searchView.classList.remove('hide');
  elements.resultsView.classList.remove('show');
  elements.backBtn.classList.remove('show');
  elements.clearBtn.classList.remove('show');
  elements.dropdown.classList.remove('show');

  // Set sessionStorage flag to prevent auto-open for rest of session
  sessionStorage.setItem('breedSearchModalClosed', 'true');
  console.log('Breed Search - Modal closed, set session flag to prevent auto-open');

  // Clear auto-open timer if it's still pending
  if (autoOpenTimer) {
    clearTimeout(autoOpenTimer);
    autoOpenTimer = null;
  }

  // Trigger close event for parent window/modal
  window.dispatchEvent(new CustomEvent('breed-search:close'));
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
